import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import { fetchAdmin, postAdmin, toErrorMessage } from '../../../admin/api';
import { fDateTime } from '../../../utils/formatTime';
import {
  AdminActionBar,
  AdminDataTable,
  AdminDetailPanel,
  AdminNoDataState,
  AdminStatusChip,
  AdminTimeline,
} from '../shared';

const DRAWER_WIDTH = 720;

const ACTIONABLE_RECOVERY_STATUSES = new Set([
  'blocked_aml',
  'blocked_on_hold',
  'recovery_in_progress',
  'recovery_failed',
]);

const ACTIONABLE_DISPATCH_STATUSES = new Set(['pending_dispatch', 'dispatch_failed']);

const LOOKUP_CONFIG = {
  blockchain: {
    label: 'By Blockchain Tx',
    buildPath: (value) => `/transactions/crypto_timeline/blockchain/${encodeURIComponent(value)}`,
    emptyDescription: 'The selected row does not expose a blockchain transaction id.',
  },
  quote: {
    label: 'By Quote',
    buildPath: (value) => `/transactions/crypto_timeline/quote/${encodeURIComponent(value)}`,
    emptyDescription: 'No quote id is available yet for this record.',
  },
  transaction: {
    label: 'By Lens Transaction',
    buildPath: (value) => `/transactions/crypto_timeline/transaction/${encodeURIComponent(value)}`,
    emptyDescription: 'The selected row does not expose a linked lens transaction id.',
  },
};

function formatValue(value) {
  if (value === null || value === undefined || value === '') {
    return 'N/A';
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return `${value}`;
}

function formatLabel(value) {
  if (!value) {
    return 'Unknown';
  }

  return value.replace(/_/g, ' ').replace(/\b\w/g, (match) => match.toUpperCase());
}

function sortChronologically(items = []) {
  return [...items].sort((left, right) => {
    const leftTime = new Date(left.created_at || left.updated_at || 0).getTime();
    const rightTime = new Date(right.created_at || right.updated_at || 0).getTime();
    return leftTime - rightTime;
  });
}

function buildDetailItems(record, fields) {
  if (!record) {
    return [];
  }

  return fields
    .filter((field) => record[field] !== undefined && record[field] !== null && record[field] !== '')
    .map((field) => ({
      label: formatLabel(field),
      value: formatValue(record[field]),
    }));
}

function buildRows(items = []) {
  return sortChronologically(items).map((item, index) => ({
    id: item.id || item.case_id || item.reference || item.quote_id || item.resource_id || `${index}-${item.created_at || 'row'}`,
    ...item,
  }));
}

function buildTimelineItems(items = []) {
  return sortChronologically(items).map((item, index) => ({
    id: item.id ? `${item.id}` : `${item.event_name || item.resource_id || 'event'}-${index}`,
    title: item.event_name || item.resource_type || 'Domain event',
    description: [
      item.event_scope,
      item.resource_type,
      item.resource_id,
      item.actor_user_id ? `actor ${item.actor_user_id}` : null,
    ]
      .filter(Boolean)
      .join(' • ') || 'Domain event payload returned without a summary',
    time: fDateTime(item.created_at || item.updated_at) || 'N/A',
    color: 'info',
  }));
}

function buildOutcomeBanner(actionResponse) {
  if (!actionResponse) {
    return null;
  }

  switch (actionResponse.action) {
    case 'noop':
      return {
        severity: 'info',
        title: 'No recovery action required',
        message: actionResponse.reason
          ? `The backend returned noop with reason ${actionResponse.reason}.`
          : 'The backend returned noop, so no additional work was queued.',
      };
    case 'case_only':
      return {
        severity: 'warning',
        title: 'Review case created',
        message: actionResponse.case_id
          ? `The backend created case ${actionResponse.case_id} for follow-up.`
          : 'The backend returned case_only without a queued recovery dispatch.',
      };
    case 'recover_blocked_deposit':
      return {
        severity: 'success',
        title: 'Blocked deposit recovery accepted',
        message: 'The backend queued recovery work for the blocked deposit.',
      };
    case 'redispatch_conversion':
      return {
        severity: 'success',
        title: 'Conversion redispatch accepted',
        message: 'The backend safely queued the conversion handoff again.',
      };
    case 'retry_failed_swap':
      return {
        severity: 'success',
        title: 'Failed swap retry accepted',
        message: 'The backend accepted the retry request for the failed swap.',
      };
    default:
      return {
        severity: 'info',
        title: 'Recovery response received',
        message: `The backend returned action ${actionResponse.action || 'unknown'}.`,
      };
  }
}

function JsonDialog({ open, title, value, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography
          component="pre"
          variant="body2"
          sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {JSON.stringify(value, null, 2)}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

JsonDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
};

JsonDialog.defaultProps = {
  value: null,
};

function RecoveryDialog({ open, form, loading, onChange, onClose, onConfirm }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Confirm blockchain recovery action</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This runs the documented recovery endpoint for the selected blockchain transaction row.
          </Typography>
          <TextField
            label="Reason (optional)"
            value={form.reason}
            placeholder="ops_release"
            onChange={(event) => onChange('reason', event.target.value)}
            fullWidth
          />
          <TextField
            label="Actor (optional)"
            value={form.actor}
            placeholder="staff_id_or_display_identifier"
            onChange={(event) => onChange('actor', event.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton loading={loading} color="warning" variant="contained" onClick={onConfirm}>
          Run recovery
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

RecoveryDialog.propTypes = {
  form: PropTypes.shape({
    actor: PropTypes.string,
    reason: PropTypes.string,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

function LoadingState() {
  return (
    <Stack spacing={2}>
      <Skeleton variant="rounded" height={92} />
      <Skeleton variant="rounded" height={160} />
      <Skeleton variant="rounded" height={220} />
      <Skeleton variant="rounded" height={220} />
    </Stack>
  );
}

export default function CryptoOpsDrawer({ open, onClose, row }) {
  const [activeTab, setActiveTab] = useState('blockchain');
  const [states, setStates] = useState({
    blockchain: { loading: false, error: null, data: null },
    quote: { loading: false, error: null, data: null },
    transaction: { loading: false, error: null, data: null },
  });
  const [recoveryDialogOpen, setRecoveryDialogOpen] = useState(false);
  const [recoveryForm, setRecoveryForm] = useState({ reason: '', actor: '' });
  const [recoveryState, setRecoveryState] = useState({ loading: false, error: null, data: null });
  const [jsonDialog, setJsonDialog] = useState({ open: false, title: '', value: null });

  const blockchainIdentifier = row?.id || row?.blockchain_tx_id || null;
  const blockchainData = states.blockchain.data?.data || null;
  const derivedTransactionId = blockchainData?.transaction?.id || null;
  const derivedQuoteId = blockchainData?.swaps?.find((item) => item.quote_id)?.quote_id || null;
  const transactionIdentifier = row?.transaction_id || row?.lens_transaction_id || derivedTransactionId || null;
  const quoteIdentifier = row?.quote_id || derivedQuoteId || null;

  const identifiers = useMemo(
    () => ({
      blockchain: blockchainIdentifier,
      quote: quoteIdentifier,
      transaction: transactionIdentifier,
    }),
    [blockchainIdentifier, quoteIdentifier, transactionIdentifier]
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setActiveTab('blockchain');
    setStates({
      blockchain: { loading: false, error: null, data: null },
      quote: { loading: false, error: null, data: null },
      transaction: { loading: false, error: null, data: null },
    });
    setRecoveryDialogOpen(false);
    setRecoveryForm({ reason: '', actor: '' });
    setRecoveryState({ loading: false, error: null, data: null });
    setJsonDialog({ open: false, title: '', value: null });
  }, [open, row?.id]);

  useEffect(() => {
    let active = true;
    const identifier = identifiers[activeTab];
    const tabState = states[activeTab];

    if (!open || !identifier || tabState.loading || tabState.data || tabState.error) {
      return () => {
        active = false;
      };
    }

    setStates((current) => ({
      ...current,
      [activeTab]: {
        loading: true,
        error: null,
        data: null,
      },
    }));

    const load = async () => {
      try {
        const response = await fetchAdmin(LOOKUP_CONFIG[activeTab].buildPath(identifier));

        if (!active) {
          return;
        }

        setStates((current) => ({
          ...current,
          [activeTab]: {
            loading: false,
            error: null,
            data: response,
          },
        }));
      } catch (error) {
        if (!active) {
          return;
        }

        setStates((current) => ({
          ...current,
          [activeTab]: {
            loading: false,
            error: toErrorMessage(error),
            data: null,
          },
        }));
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [activeTab, identifiers, open]);

  const currentState = states[activeTab];
  const timelineData = currentState.data?.data || null;
  const blockchain = timelineData?.blockchain || null;
  const transaction = timelineData?.transaction || null;

  const summaryItems = [
    { label: 'Blockchain Row ID', value: formatValue(blockchainIdentifier) },
    { label: 'Lens Transaction ID', value: formatValue(transactionIdentifier) },
    { label: 'Quote ID', value: formatValue(quoteIdentifier) },
    { label: 'Visible TxID', value: formatValue(row?.txid) },
  ];

  const blockchainItems = buildDetailItems(blockchain, [
    'id',
    'txid',
    'coin_id',
    'amount',
    'status',
    'swap_status',
    'recovery_status',
    'recovery_reason',
    'conversion_dispatch_status',
    'conversion_dispatch_error',
    'confirmations',
    'target_confirmations',
    'created_at',
    'updated_at',
  ]);

  const transactionItems = buildDetailItems(transaction, [
    'id',
    'reference',
    'transaction_type',
    'status',
    'description',
    'user_id',
    'created_at',
    'updated_at',
  ]);

  const recoveryItems = buildDetailItems(recoveryState.data?.data, [
    'action',
    'reason',
    'blockchain_tx_id',
    'transaction_id',
    'case_id',
    'outbox_job_id',
  ]);

  const recoveryStatus = blockchain?.recovery_status || '';
  const dispatchStatus = blockchain?.conversion_dispatch_status || '';
  const recoveryActionable =
    Boolean(blockchain?.id) &&
    (ACTIONABLE_RECOVERY_STATUSES.has(recoveryStatus) || ACTIONABLE_DISPATCH_STATUSES.has(dispatchStatus));

  const outcomeBanner = buildOutcomeBanner(recoveryState.data?.data);

  const openPayloadDialog = (title, value) => {
    setJsonDialog({ open: true, title, value });
  };

  const handleRecoveryConfirm = async () => {
    if (!blockchain?.id) {
      return;
    }

    const payload = {};

    if (recoveryForm.reason.trim()) {
      payload.reason = recoveryForm.reason.trim();
    }

    if (recoveryForm.actor.trim()) {
      payload.actor = recoveryForm.actor.trim();
    }

    setRecoveryState({ loading: true, error: null, data: null });

    try {
      const response = await postAdmin(`/transactions/blockchain/${blockchain.id}/recover`, payload);
      setRecoveryState({ loading: false, error: null, data: response });
      setRecoveryDialogOpen(false);
    } catch (error) {
      setRecoveryState({ loading: false, error: toErrorMessage(error), data: null });
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', lg: DRAWER_WIDTH },
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2} sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={1}>
              <Typography variant="h6">Crypto Ops Drawer</Typography>
              <Typography variant="body2" color="text.secondary">
                Timeline and recovery tools for the selected blockchain transaction row without manual id entry.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                {row?.status ? <AdminStatusChip status={row.status} /> : null}
                {row?.swap_status ? <AdminStatusChip status={row.swap_status} /> : null}
                {row?.recovery_status ? <AdminStatusChip status={row.recovery_status} /> : null}
              </Stack>
            </Stack>
            <IconButton onClick={onClose} aria-label="Close crypto ops drawer">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Divider />

          <Box sx={{ px: 3, py: 2 }}>
            <AdminDetailPanel title="Selected Row" items={summaryItems} />
          </Box>

          <Box sx={{ px: 3 }}>
            <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} variant="scrollable" allowScrollButtonsMobile>
              <Tab value="blockchain" label="By Blockchain Tx" />
              <Tab value="quote" label="By Quote" disabled={!quoteIdentifier} />
              <Tab value="transaction" label="By Lens Transaction" disabled={!transactionIdentifier} />
            </Tabs>
          </Box>

          <Divider />

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 3 }}>
            {!identifiers[activeTab] ? (
              <AdminNoDataState
                title={`${LOOKUP_CONFIG[activeTab].label} unavailable`}
                description={LOOKUP_CONFIG[activeTab].emptyDescription}
                icon="solar:database-bold"
              />
            ) : null}

            {identifiers[activeTab] && currentState.loading ? <LoadingState /> : null}

            {identifiers[activeTab] && !currentState.loading && currentState.error ? (
              <Alert severity="error" variant="outlined">
                <AlertTitle>Timeline request failed</AlertTitle>
                {currentState.error}
              </Alert>
            ) : null}

            {identifiers[activeTab] && !currentState.loading && !currentState.error && !timelineData ? (
              <AdminNoDataState
                title="No crypto timeline loaded"
                description="The selected lookup did not return timeline data."
                icon="solar:database-bold"
              />
            ) : null}

            {timelineData ? (
              <Stack spacing={3}>
                <Alert severity="info" variant="outlined">
                  <AlertTitle>{LOOKUP_CONFIG[activeTab].label}</AlertTitle>
                  Loaded using identifier {formatValue(identifiers[activeTab])}.
                </Alert>

                {recoveryState.error ? (
                  <Alert severity="error" variant="outlined">
                    <AlertTitle>Recovery request failed</AlertTitle>
                    {recoveryState.error}
                  </Alert>
                ) : null}

                {outcomeBanner ? (
                  <Alert severity={outcomeBanner.severity} variant="outlined">
                    <AlertTitle>{outcomeBanner.title}</AlertTitle>
                    {outcomeBanner.message}
                  </Alert>
                ) : null}

                {recoveryItems.length ? <AdminDetailPanel title="Last Recovery Outcome" items={recoveryItems} /> : null}

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {blockchain?.status ? <AdminStatusChip status={blockchain.status} /> : null}
                  {blockchain?.swap_status ? <AdminStatusChip status={blockchain.swap_status} /> : null}
                  {blockchain?.recovery_status ? <AdminStatusChip status={blockchain.recovery_status} /> : null}
                  {blockchain?.conversion_dispatch_status ? (
                    <AdminStatusChip status={blockchain.conversion_dispatch_status} />
                  ) : null}
                  {transaction?.status ? <AdminStatusChip status={transaction.status} /> : null}
                </Stack>

                {recoveryActionable ? (
                  <AdminActionBar
                    title="Recovery Action"
                    description="The current blockchain recovery or dispatch state is actionable under the documented contract."
                    actions={[
                      {
                        label: 'Open Recovery Dialog',
                        color: 'warning',
                        variant: 'contained',
                        icon: 'solar:restart-bold',
                      },
                    ]}
                    onActionClick={() => setRecoveryDialogOpen(true)}
                  />
                ) : null}

                <AdminDetailPanel title="Blockchain Anchor" items={blockchainItems.length ? blockchainItems : [{ label: 'Blockchain', value: 'N/A' }]} />
                <AdminDetailPanel title="Linked Lens Transaction" items={transactionItems.length ? transactionItems : [{ label: 'Transaction', value: 'Not linked' }]} />

                <AdminDataTable
                  title="Swaps"
                  description="Swap records returned by the crypto timeline contract."
                  columns={[
                    { field: 'quote_id', headerName: 'Quote ID' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'is_finalized', headerName: 'Finalized' },
                    { field: 'from_currency', headerName: 'From' },
                    { field: 'to_currency', headerName: 'To' },
                    { field: 'settlement_amount', headerName: 'Settlement' },
                    { field: 'created_at', headerName: 'Created' },
                  ]}
                  rows={buildRows(timelineData.swaps || []).map((item) => ({
                    ...item,
                    is_finalized: Number(item.is_finalized) === 1 ? 'Yes' : 'No',
                    created_at: fDateTime(item.created_at) || 'N/A',
                  }))}
                  emptyTitle="No swaps"
                  emptyDescription="No swap rows were returned for this timeline."
                />

                <AdminDataTable
                  title="Ledger Entries"
                  description="Ledger entry rows linked to the timeline."
                  columns={[
                    { field: 'wallet_type', headerName: 'Wallet Type' },
                    { field: 'entry_type', headerName: 'Entry Type' },
                    { field: 'amount', headerName: 'Amount' },
                    { field: 'balance_before', headerName: 'Before' },
                    { field: 'balance_after', headerName: 'After' },
                    {
                      field: 'meta',
                      headerName: 'Meta',
                      render: (item) =>
                        item.meta ? (
                          <Button size="small" onClick={() => openPayloadDialog('Ledger entry meta', item.meta)}>
                            View payload
                          </Button>
                        ) : (
                          'N/A'
                        ),
                    },
                    { field: 'created_at', headerName: 'Created' },
                  ]}
                  rows={buildRows(timelineData.ledger_entries || []).map((item) => ({
                    ...item,
                    created_at: fDateTime(item.created_at) || 'N/A',
                  }))}
                  emptyTitle="No ledger entries"
                  emptyDescription="No ledger entries were returned for this timeline."
                />

                <AdminDataTable
                  title="Sweeps"
                  description="Sweep records in chronological order."
                  columns={[
                    { field: 'reference', headerName: 'Reference' },
                    { field: 'amount', headerName: 'Amount' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'trigger_source', headerName: 'Trigger Source' },
                    { field: 'sweep_reason', headerName: 'Sweep Reason' },
                    {
                      field: 'provider_response',
                      headerName: 'Provider Response',
                      render: (item) =>
                        item.provider_response ? (
                          <Button size="small" onClick={() => openPayloadDialog('Sweep provider response', item.provider_response)}>
                            View payload
                          </Button>
                        ) : (
                          'N/A'
                        ),
                    },
                    { field: 'created_at', headerName: 'Created' },
                  ]}
                  rows={buildRows(timelineData.sweeps || []).map((item) => ({
                    ...item,
                    created_at: fDateTime(item.created_at) || 'N/A',
                  }))}
                  emptyTitle="No sweeps"
                  emptyDescription="No sweep records were returned for this timeline."
                />

                <AdminDataTable
                  title="Bank Transfers"
                  description="Bank transfer records returned by the timeline."
                  columns={[
                    { field: 'reference', headerName: 'Reference' },
                    { field: 'amount', headerName: 'Amount' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'provider', headerName: 'Provider' },
                    { field: 'created_at', headerName: 'Created' },
                    { field: 'updated_at', headerName: 'Updated' },
                  ]}
                  rows={buildRows(timelineData.bank_transfers || []).map((item) => ({
                    ...item,
                    created_at: fDateTime(item.created_at) || 'N/A',
                    updated_at: fDateTime(item.updated_at) || 'N/A',
                  }))}
                  emptyTitle="No bank transfers"
                  emptyDescription="No bank transfer rows were returned for this timeline."
                />

                <AdminDataTable
                  title="Cases"
                  description="Review cases linked to the selected blockchain row."
                  columns={[
                    { field: 'case_id', headerName: 'Case ID' },
                    { field: 'case_type', headerName: 'Case Type' },
                    { field: 'status', headerName: 'Status' },
                    { field: 'priority', headerName: 'Priority' },
                    { field: 'summary', headerName: 'Summary' },
                    { field: 'created_at', headerName: 'Created' },
                  ]}
                  rows={buildRows(timelineData.cases || []).map((item) => ({
                    ...item,
                    created_at: fDateTime(item.created_at) || 'N/A',
                  }))}
                  emptyTitle="No cases"
                  emptyDescription="No cases were returned for this timeline."
                />

                {(timelineData.domain_events || []).length ? (
                  <AdminTimeline title="Domain Events" items={buildTimelineItems(timelineData.domain_events)} />
                ) : (
                  <AdminNoDataState
                    title="No domain events"
                    description="The timeline response did not include domain events."
                    icon="solar:playlist-minimalistic-bold"
                  />
                )}
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Drawer>

      <RecoveryDialog
        open={recoveryDialogOpen}
        form={recoveryForm}
        loading={recoveryState.loading}
        onChange={(field, value) => setRecoveryForm((current) => ({ ...current, [field]: value }))}
        onClose={() => setRecoveryDialogOpen(false)}
        onConfirm={handleRecoveryConfirm}
      />

      <JsonDialog
        open={jsonDialog.open}
        title={jsonDialog.title}
        value={jsonDialog.value}
        onClose={() => setJsonDialog({ open: false, title: '', value: null })}
      />
    </>
  );
}

CryptoOpsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  row: PropTypes.object,
};

CryptoOpsDrawer.defaultProps = {
  row: null,
};
